import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GitBranch,
  RefreshCw,
  Search,
  Settings,
  ChevronDown,
  ChevronRight,
  Rocket,
  CheckCircle2,
  XCircle,
  Minus,
  ExternalLink,
  Copy,
  Check,
  Plus,
  X,
  Tag,
  Lock,
  Unlock,
  UploadCloud,
} from 'lucide-react';
import { api } from '../services/apiService';
import { getAdminProductConfig } from '../config/productConfig';
import { StaffPage } from '../components/staff/StaffChrome';
import { useDeployAll } from '../contexts/DeployAllContext';
import { PipelineStatus } from '../components/PipelineStatus';
import { PipelineTooltip, type BranchGraph } from '../components/PipelineTooltip';

// ── Types ──────────────────────────────────────────────────────────────────────

interface RepoTag {
  text: string;
  color: string;
}

interface WorkflowRunJob {
  id: number;
  name: string;
  status: string;
  conclusion: string | null;
  started_at: string;
  completed_at: string | null;
  steps: {
    name: string;
    status: string;
    conclusion: string | null;
    number: number;
    started_at?: string | null;
    completed_at?: string | null;
  }[];
}

interface RepoStatus {
  repoName: string;
  productId: string;
  category: string;
  displayName?: string;
  tags?: RepoTag[];
  deployLocked?: boolean;
  devStatus: 'success' | 'failure' | 'running' | 'none';
  prodStatus: 'success' | 'failure' | 'running' | 'none';
  lastDevRun?: any;
  lastProdRun?: any;
  devBuildStatus?: 'success' | 'failure' | 'running' | 'none';
  devDeployStatus?: 'success' | 'failure' | 'running' | 'none';
  prodBuildStatus?: 'success' | 'failure' | 'running' | 'none';
  prodDeployStatus?: 'success' | 'failure' | 'running' | 'none';
  devBuildJobs?: WorkflowRunJob[];
  prodBuildJobs?: WorkflowRunJob[];
  devDeployByProduct?: Record<string, string>;
  prodDeployByProduct?: Record<string, string>;
  devDeployTimestamps?: Record<string, string | null>;
  prodDeployTimestamps?: Record<string, string | null>;
}

interface DashboardStats {
  totalRepos: number;
  deploys24h: number;
  successRate: number;
  failed24h: number;
  categories: { database: number; infrastructure: number; service: number; frontend: number; document: number; library: number; script: number };
}

interface GroupedRepos {
  database: RepoStatus[];
  infrastructure: RepoStatus[];
  service: RepoStatus[];
  frontend: RepoStatus[];
  document: RepoStatus[];
  library: RepoStatus[];
  script: RepoStatus[];
}

// ── Constants ──────────────────────────────────────────────────────────────────

const CATEGORY_ORDER = ['database', 'infrastructure', 'service', 'frontend', 'library', 'document', 'script', 'data-migration', 'asset'] as const;
const CATEGORY_LABELS: Record<string, string> = {
  database: 'Database',
  infrastructure: 'Infrastructure',
  service: 'Services',
  frontend: 'Frontend',
  document: 'Documents',
  library: 'Libraries',
  script: 'Scripts',
  'data-migration': 'Data Migration Tools',
  asset: 'Assets',
};

const TAG_COLORS = [
  { name: 'blue', bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-500/30', dot: 'bg-blue-400' },
  { name: 'green', bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-500/30', dot: 'bg-green-400' },
  { name: 'red', bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30', dot: 'bg-red-400' },
  { name: 'yellow', bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-500/30', dot: 'bg-yellow-400' },
  { name: 'purple', bg: 'bg-rose-500/20', text: 'text-rose-300', border: 'border-rose-500/30', dot: 'bg-rose-400' },
  { name: 'pink', bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-500/30', dot: 'bg-pink-400' },
  { name: 'orange', bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-500/30', dot: 'bg-orange-400' },
  { name: 'cyan', bg: 'bg-cyan-500/20', text: 'text-cyan-300', border: 'border-cyan-500/30', dot: 'bg-cyan-400' },
];

const PRODUCT_REGIONS = [
  { id: 'cafemaster', region: 'us-east-2', label: 'CafeMaster', dotColor: 'bg-amber-500' },
  { id: 'agenticpos', region: 'us-west-1', label: 'AgenticPOS', dotColor: 'bg-emerald-500' },
  { id: 'agenticrealestate', region: 'us-west-2', label: 'Realestate', dotColor: 'bg-blue-500' },
];

const POLL_INTERVAL = 30000; // 30 seconds — GitHub API rate limit is 5000/hour

function getTagStyle(color: string) {
  return TAG_COLORS.find(c => c.name === color) || TAG_COLORS[0];
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function JobTooltip({ jobs, label }: { jobs?: WorkflowRunJob[]; label: string }) {
  if (!jobs || jobs.length === 0) return null;
  return (
    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 bg-stone-100 border border-stone-300 rounded-lg shadow-xl p-3 text-xs pointer-events-none">
      <p className="font-semibold text-stone-600 mb-2">{label}</p>
      {jobs.map((job) => (
        <div key={job.id} className="mb-2 last:mb-0">
          <div className="flex items-center gap-1.5 mb-1">
            <StatusIcon status={job.conclusion === 'success' ? 'success' : job.conclusion === 'failure' ? 'failure' : job.status === 'in_progress' ? 'running' : 'none'} size="w-3 h-3" />
            <span className="text-gray-900 font-medium">{job.name}</span>
          </div>
          {job.steps && job.steps.length > 0 && (
            <div className="ml-4 space-y-0.5">
              {job.steps.map((step) => (
                <div key={step.number} className="flex items-center gap-1">
                  {step.conclusion === 'success' ? (
                    <CheckCircle2 className="w-2.5 h-2.5 text-green-500 shrink-0" />
                  ) : step.conclusion === 'failure' ? (
                    <XCircle className="w-2.5 h-2.5 text-red-500 shrink-0" />
                  ) : step.status === 'in_progress' ? (
                    <RubyGemMark size={10} drawing />
                  ) : (
                    <Minus className="w-2.5 h-2.5 text-stone-600 shrink-0" />
                  )}
                  <span className={`truncate ${step.conclusion === 'failure' ? 'text-red-300' : 'text-stone-400'}`}>{step.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Resolve the build-stage status for the pipeline icon.
 *
 * Falls back to the overall run status when no explicit build status exists,
 * AND guards against a stale "running" build: a build cannot still be running
 * once the run itself has reached a terminal state (success/failure). Without
 * this guard a missed workflow_run completion webhook leaves buildStatus stuck
 * at "running" and the build icon spins forever ("spinning for days").
 */
function resolveBuildStatus(buildStatus?: string, runStatus?: string): string {
  const build = buildStatus && buildStatus !== 'none' ? buildStatus : runStatus;
  if (build === 'running' && (runStatus === 'success' || runStatus === 'failure')) {
    return runStatus;
  }
  return build || 'none';
}

/** Tailwind box -> px, so the running gem sits in the same footprint as the icon it replaces. */
const GEM_PX: Record<string, number> = { 'w-2.5 h-2.5': 10, 'w-3 h-3': 12, 'w-3.5 h-3.5': 14, 'w-4 h-4': 16, 'w-5 h-5': 20 };

function StatusIcon({ status, size = 'w-4 h-4' }: { status: string; size?: string }) {
  switch (status) {
    case 'success':
      return <CheckCircle2 className={`${size} text-green-400`} />;
    case 'failure':
      return <XCircle className={`${size} text-red-400`} />;
    case 'running':
      return <RubyGemMark size={GEM_PX[size] ?? 16} drawing />;
    default:
      return <Minus className={`${size} text-stone-600`} />;
  }
}

function TagPill({ tag, onRemove }: { tag: RepoTag; onRemove?: () => void }) {
  const style = getTagStyle(tag.color);
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${style.bg} ${style.text} border ${style.border}`}>
      {tag.text}
      {onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="hover:opacity-70">
          <X className="w-2.5 h-2.5" />
        </button>
      )}
    </span>
  );
}

function TagEditor({ tags, onSave, onCancel }: { tags: RepoTag[]; onSave: (tags: RepoTag[]) => void; onCancel: () => void }) {
  const [editTags, setEditTags] = useState<RepoTag[]>([...tags]);
  const [newText, setNewText] = useState('');
  const [newColor, setNewColor] = useState('blue');
  const inputRef = useRef<HTMLInputElement>(null);

  const addTag = () => {
    if (!newText.trim()) return;
    setEditTags([...editTags, { text: newText.trim(), color: newColor }]);
    setNewText('');
    inputRef.current?.focus();
  };

  const removeTag = (idx: number) => {
    setEditTags(editTags.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    // Auto-add any typed text before saving
    const finalTags = [...editTags];
    if (newText.trim()) {
      finalTags.push({ text: newText.trim(), color: newColor });
    }
    onSave(finalTags);
  };

  return (
    <div className="absolute z-50 top-full left-0 mt-1 w-72 bg-stone-100 border border-stone-300 rounded-lg shadow-xl p-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap gap-1 mb-2 min-h-[24px]">
        {editTags.map((tag, i) => (
          <TagPill key={i} tag={tag} onRemove={() => removeTag(i)} />
        ))}
        {editTags.length === 0 && <span className="text-stone-500 text-xs">No tags</span>}
      </div>
      <div className="flex items-center gap-1 mb-2">
        <input
          ref={inputRef}
          type="text"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addTag()}
          placeholder="Tag text..."
          className="flex-1 px-2 py-1 bg-white border border-stone-200 rounded text-xs text-gray-900 placeholder-stone-500"
          autoFocus
        />
        <button onClick={addTag} className="p-1 text-green-400 hover:text-green-300"><Plus className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex items-center gap-1 mb-3">
        {TAG_COLORS.map((c) => (
          <button
            key={c.name}
            onClick={() => setNewColor(c.name)}
            className={`w-5 h-5 rounded-full ${c.dot} ${newColor === c.name ? 'ring-2 ring-white ring-offset-1 ring-offset-white' : 'opacity-60 hover:opacity-100'}`}
          />
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-1 text-xs bg-stone-100 text-stone-600 rounded hover:bg-stone-200">Cancel</button>
        <button onClick={handleSave} className="flex-1 py-1 text-xs bg-admin-primary text-white rounded hover:bg-admin-primary/90">Save</button>
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="grid grid-cols-[1fr_100px_290px_290px_80px] gap-2 px-4 py-3 items-center border-b border-stone-200/30">
      <div className="space-y-1.5">
        <div className="h-4 w-36 rounded skeleton-shimmer" />
        <div className="h-3 w-24 rounded skeleton-shimmer" />
      </div>
      <div className="flex gap-1 justify-center">
        <div className="h-5 w-12 rounded skeleton-shimmer" />
      </div>
      <div className="space-y-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full skeleton-shimmer" />
            <div className="h-2 w-10 rounded skeleton-shimmer" />
            <div className="w-5 h-5 rounded-full skeleton-shimmer" />
            <div className="h-1 w-5 rounded skeleton-shimmer" />
            <div className="w-5 h-5 rounded-full skeleton-shimmer" />
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full skeleton-shimmer" />
            <div className="h-2 w-10 rounded skeleton-shimmer" />
            <div className="w-5 h-5 rounded-full skeleton-shimmer" />
            <div className="h-1 w-5 rounded skeleton-shimmer" />
            <div className="w-5 h-5 rounded-full skeleton-shimmer" />
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-1">
        <div className="w-6 h-6 rounded skeleton-shimmer" />
        <div className="w-6 h-6 rounded skeleton-shimmer" />
      </div>
    </div>
  );
}

function SkeletonStatsCard() {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4">
      <div className="h-3 w-16 rounded skeleton-shimmer mb-2" />
      <div className="h-7 w-12 rounded skeleton-shimmer" />
    </div>
  );
}

function SkeletonCategory({ count = 3 }: { count?: number }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3">
        <div className="h-4 w-4 rounded skeleton-shimmer" />
        <div className="h-3 w-28 rounded skeleton-shimmer" />
      </div>
      <div className="border-t border-stone-200">
        <div className="grid grid-cols-[1fr_100px_290px_290px_80px] gap-2 px-4 py-2 border-b border-stone-200/50">
          {[80, 40, 100, 100, 50].map((w, i) => (
            <div key={i} className={`h-2.5 w-${w >= 80 ? '20' : '12'} rounded skeleton-shimmer`} />
          ))}
        </div>
        {Array.from({ length: count }).map((_, i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    </div>
  );
}

// ── Confirm Lock/Unlock Modal (type confirmation) ──────────────────────────────

function ConfirmLockModal({ repoName, locking, onSuccess, onClose }: {
  repoName: string;
  locking: boolean;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const requiredText = locking ? 'LOCK' : 'UNLOCK';
  const isValid = confirmation === requiredText;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError('');
    try {
      await api.devops.toggleDeployLock({ repoName, locked: locking, confirmation });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to update deploy lock');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-stone-200 rounded-xl p-6 max-w-sm w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {locking ? <Lock className="w-5 h-5 text-red-400" /> : <Unlock className="w-5 h-5 text-green-400" />}
            <h3 className="text-base font-semibold text-gray-900">
              {locking ? 'Lock' : 'Unlock'} Deploy
            </h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-gray-900"><X className="w-4 h-4" /></button>
        </div>

        <p className="text-stone-400 text-sm mb-1">
          {locking ? 'Locking' : 'Unlocking'} deploys for:
        </p>
        <p className="text-gray-900 font-mono text-sm mb-4">{repoName}</p>

        <p className="text-stone-400 text-sm mb-3">
          Type <span className="text-gray-900 font-bold">{requiredText}</span> to confirm:
        </p>
        <input
          type="text"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && isValid && handleSubmit()}
          placeholder={requiredText}
          className={`w-full px-4 py-3 bg-stone-100 border rounded-lg text-gray-900 text-center text-lg font-mono tracking-wider placeholder-stone-600 mb-3 ${
            confirmation && !isValid ? 'border-red-500/50' : isValid ? 'border-green-500/50' : 'border-stone-300'
          }`}
          autoFocus
        />
        {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 bg-stone-100 text-stone-600 rounded-lg hover:bg-stone-200 text-sm">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || submitting}
            className={`flex-1 py-2 text-white rounded-lg text-sm disabled:opacity-50 ${
              locking ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {submitting ? <span className="inline-flex items-center gap-2"><RubyGemMark size={16} drawing color="currentColor" />Processing...</span> : locking ? 'Lock Deploys' : 'Unlock Deploys'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pipeline Legend ────────────────────────────────────────────────────────────

function PipelineLegend() {
  return (
    <div className="bg-white border border-stone-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
      <div className="flex items-center gap-1">
        <UploadCloud className="w-3.5 h-3.5 text-stone-600" />
        <span className="text-[10px] text-stone-400">No runs</span>
      </div>
      <div className="flex items-center gap-1">
        <RubyGemMark size={14} drawing />
        <span className="text-[10px] text-stone-400">Running</span>
      </div>
      <div className="flex items-center gap-1">
        <UploadCloud className="w-3.5 h-3.5 text-blue-400" />
        <span className="text-[10px] text-stone-400">Built · not deployed</span>
      </div>
      <div className="flex items-center gap-1">
        <UploadCloud className="w-3.5 h-3.5 text-green-400" />
        <span className="text-[10px] text-stone-400">Deployed</span>
      </div>
      <div className="flex items-center gap-1">
        <XCircle className="w-3.5 h-3.5 text-red-400" />
        <span className="text-[10px] text-stone-400">Failed</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-0.5 h-4 rounded-full bg-emerald-500" />
        <span className="text-[10px] text-stone-400">Dev</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-0.5 h-4 rounded-full bg-rose-500" />
        <span className="text-[10px] text-stone-400">Prod</span>
      </div>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────

function DeploymentDashboard() {
  const navigate = useNavigate();
  const currentProduct = getAdminProductConfig().id;
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [grouped, setGrouped] = useState<GroupedRepos | null>(null);
  const [allRepos, setAllRepos] = useState<RepoStatus[]>([]);
  const [org, setOrg] = useState('');
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [productFilter, setProductFilter] = useState(currentProduct);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([...CATEGORY_ORDER]);
  const [deployingRepo, setDeployingRepo] = useState<string | null>(null);
  const [deployEnv, setDeployEnv] = useState<'dev' | 'prod'>('dev');
  const [deployProduct] = useState<string>(currentProduct);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState<RepoStatus | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configPat, setConfigPat] = useState('');
  const [configOrg, setConfigOrg] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [needsConfig, setNeedsConfig] = useState(false);
  const [copiedRepo, setCopiedRepo] = useState<string | null>(null);
  const [editingTagsFor, setEditingTagsFor] = useState<string | null>(null);
  const [lockModal, setLockModal] = useState<{ repoName: string; locking: boolean } | null>(null);
  const { batch: deployAllBatch, openPreview: openDeployAll, expand: expandDeployAll } = useDeployAll();
  // Start a Deploy All preview — or, if a batch is already active (running or
  // halted), just reopen it. Only one batch runs at a time.
  const handleDeployAll = (environment: 'dev' | 'prod') => {
    if (deployAllBatch && (deployAllBatch.status === 'running' || deployAllBatch.status === 'failed')) {
      expandDeployAll();
      return;
    }
    openDeployAll({ environment, product: currentProduct, org });
  };
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ done: 0, total: 0 });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Merge repos: update with new data but preserve locally-changed fields (tags)
  const mergeRepos = useCallback((
    prevRepos: RepoStatus[],
    newRepos: RepoStatus[]
  ): RepoStatus[] => {
    if (prevRepos.length === 0) return newRepos;
    return newRepos.map(nr => {
      const pr = prevRepos.find(p => p.repoName === nr.repoName);
      if (!pr) return nr;
      // Always take the latest status data from API, but preserve local tags if API tags are empty
      return {
        ...nr,
        tags: (nr.tags && nr.tags.length > 0) ? nr.tags : pr.tags,
      };
    });
  }, []);

  const mergeGrouped = useCallback((
    prev: GroupedRepos,
    next: GroupedRepos
  ): GroupedRepos => {
    return {
      database: mergeRepos(prev.database, next.database),
      infrastructure: mergeRepos(prev.infrastructure, next.infrastructure),
      service: mergeRepos(prev.service, next.service),
      frontend: mergeRepos(prev.frontend, next.frontend),
      document: mergeRepos(prev.document || [], next.document || []),
      library: mergeRepos(prev.library || [], next.library || []),
      script: mergeRepos(prev.script || [], next.script || []),
    };
  }, [mergeRepos]);

  const fetchData = useCallback(async (background = false) => {
    if (!background) {
      setLoading(true);
      setError('');
    }
    try {
      // All calls use silent mode — skeleton loading handles the visual feedback
      // Skip config check on background polls
      if (!background) {
        const config = await api.devops.getConfigSilent() as any;
        if (!config.configured) {
          setNeedsConfig(true);
          setConfigOrg(config.org || '');
          setLoading(false);
          return;
        }
        setNeedsConfig(false);
      }

      const [statsData, reposData] = await Promise.all([
        api.devops.getDashboardStatsSilent(productFilter !== 'all' ? productFilter : undefined),
        api.devops.listReposSilent({
          product: productFilter !== 'all' ? productFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
        }),
      ]) as [DashboardStats, { repos: RepoStatus[]; grouped: GroupedRepos; org: string }];

      setStats(statsData);
      setOrg(reposData.org);

      if (background) {
        setAllRepos(prev => mergeRepos(prev, reposData.repos));
        setGrouped(prev => prev ? mergeGrouped(prev, reposData.grouped) : reposData.grouped);
      } else {
        setAllRepos(reposData.repos);
        setGrouped(reposData.grouped);
      }
    } catch (err: any) {
      if (!background) {
        setError(err.message || 'Failed to load deployment data');
      }
    } finally {
      if (!background) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  }, [productFilter, categoryFilter, mergeRepos, mergeGrouped]);

  // Initial load
  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  // Auto-refresh polling — only start after initial load completes
  useEffect(() => {
    if (needsConfig || initialLoad) return;
    pollRef.current = setInterval(() => {
      fetchData(true);
    }, POLL_INTERVAL);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchData, needsConfig, initialLoad]);

  const handleDeploy = async () => {
    if (!selectedRepo || !org) return;
    setDeployingRepo(selectedRepo.repoName);
    setShowDeployModal(false);
    try {
      await api.devops.triggerDeploy(org, selectedRepo.repoName, { product: deployProduct, environment: deployEnv });
      setTimeout(() => fetchData(true), 3000);
    } catch (err: any) {
      setError(`Deploy failed: ${err.message}`);
    } finally {
      setDeployingRepo(null);
    }
  };

  // ── Pipeline tooltip actions ────────────────────────────────────────────────
  // Start/Rerun route through the existing deploy modal so the prod
  // confirmation and the deploy lock keep applying; only Stop acts directly.

  const openDeployModalFor = (repo: RepoStatus, environment: 'dev' | 'prod') => {
    setSelectedRepo(repo);
    setDeployEnv(environment);
    setShowDeployModal(true);
  };

  const handleStopRun = async (repo: RepoStatus, environment: 'dev' | 'prod') => {
    const run = environment === 'dev' ? repo.lastDevRun : repo.lastProdRun;
    if (!org || !run?.id) return;
    try {
      await api.devops.cancelRun(org, repo.repoName, run.id);
      setTimeout(() => fetchData(true), 2000);
    } catch (err: any) {
      setError(`Cancel failed: ${err.message}`);
    }
  };

  // One branch-graph fetch per repo, reused by both the dev and prod rows.
  const branchGraphCache = useRef<Map<string, Promise<BranchGraph>>>(new Map());
  const loadBranchGraph = useCallback((repoName: string): Promise<BranchGraph> => {
    const cached = branchGraphCache.current.get(repoName);
    if (cached) return cached;
    const promise = (api.devops.getBranchGraph(org, repoName) as Promise<BranchGraph>)
      .catch(err => {
        branchGraphCache.current.delete(repoName);   // don't cache a failure
        throw err;
      });
    branchGraphCache.current.set(repoName, promise);
    return promise;
  }, [org]);

  const handleSaveConfig = async () => {
    if (!configPat) return;
    setConfigLoading(true);
    try {
      await api.devops.saveConfig({ pat: configPat, org: configOrg });
      setShowConfigModal(false);
      setConfigPat('');
      fetchData(false);
    } catch (err: any) {
      setError(`Config save failed: ${err.message}`);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleCopyRepoName = (name: string) => {
    navigator.clipboard.writeText(name);
    setCopiedRepo(name);
    setTimeout(() => setCopiedRepo(null), 2000);
  };

  const handleSaveTags = async (repoName: string, newTags: RepoTag[]) => {
    setEditingTagsFor(null);
    try {
      const registry = await api.devops.getRegistry() as any;
      const repos = registry?.repos || allRepos.map((r: RepoStatus) => ({
        repoName: r.repoName,
        productId: r.productId,
        category: r.category,
        displayName: r.displayName,
        tags: r.tags,
      }));

      const updated = repos.map((r: any) =>
        r.repoName === repoName ? { ...r, tags: newTags } : r
      );

      await api.devops.saveRegistry({ repos: updated });

      // Use functional updaters to avoid stale closure from async gap
      const updateGroup = (repos: RepoStatus[]) =>
        repos.map(r => r.repoName === repoName ? { ...r, tags: newTags } : r);
      setAllRepos(prev => prev.map(r => r.repoName === repoName ? { ...r, tags: newTags } : r));
      setGrouped(prev => prev ? {
        database: updateGroup(prev.database),
        infrastructure: updateGroup(prev.infrastructure),
        service: updateGroup(prev.service),
        frontend: updateGroup(prev.frontend),
        document: updateGroup(prev.document || []),
        library: updateGroup(prev.library || []),
        script: updateGroup(prev.script || []),
      } : prev);
    } catch (err: any) {
      setError(`Failed to save tags: ${err.message}`);
    }
  };

  const handleLockToggle = (repo: RepoStatus) => {
    setLockModal({ repoName: repo.repoName, locking: !repo.deployLocked });
  };

  const handleLockSuccess = () => {
    if (lockModal) {
      const { repoName, locking } = lockModal;
      const updateGroup = (repos: RepoStatus[]) =>
        repos.map(r => r.repoName === repoName ? { ...r, deployLocked: locking } : r);
      setAllRepos(prev => prev.map(r => r.repoName === repoName ? { ...r, deployLocked: locking } : r));
      setGrouped(prev => prev ? {
        database: updateGroup(prev.database),
        infrastructure: updateGroup(prev.infrastructure),
        service: updateGroup(prev.service),
        frontend: updateGroup(prev.frontend),
        document: updateGroup(prev.document || []),
        library: updateGroup(prev.library || []),
        script: updateGroup(prev.script || []),
      } : prev);
    }
    setLockModal(null);
  };

  const handleRefreshFromGitHub = async () => {
    if (!org || refreshing) return;
    const repos = allRepos.map(r => r.repoName);
    setRefreshing(true);
    setRefreshProgress({ done: 0, total: repos.length });
    let done = 0;
    const CONCURRENCY = 5;
    let idx = 0;
    const worker = async () => {
      while (idx < repos.length) {
        const myIdx = idx++;
        try { await api.devops.refreshRepo(org, repos[myIdx]); } catch { /* per-repo failure is non-fatal */ }
        done++;
        setRefreshProgress({ done, total: repos.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, repos.length) }, () => worker()));
    setRefreshing(false);
    await fetchData(false);
  };

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const filterRepos = (repos: RepoStatus[]) => {
    if (!search) return repos;
    const q = search.toLowerCase();
    return repos.filter(
      r =>
        r.repoName.toLowerCase().includes(q) ||
        (r.displayName || '').toLowerCase().includes(q) ||
        (r.tags || []).some(t => t.text.toLowerCase().includes(q))
    );
  };

  // Setup required screen
  if (needsConfig && !loading) {
    return (
      <StaffPage section="System" icon={GitBranch} title="Deployments" subtitle="CI/CD pipelines across all repos">
      <div className="max-w-lg mx-auto mt-10">
        <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-8 text-center">
          <GitBranch className="w-12 h-12 text-admin-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">GitHub Setup Required</h2>
          <p className="text-stone-400 mb-6">
            Configure a GitHub Personal Access Token to connect the deployment dashboard to your repositories.
          </p>
          <div className="space-y-4 text-left">
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-1">GitHub Organization</label>
              <input type="text" value={configOrg} onChange={e => setConfigOrg(e.target.value)} className="w-full px-3 py-2 bg-stone-100 border border-stone-300 rounded-lg text-gray-900" placeholder="arachchi786-beep" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-1">Personal Access Token</label>
              <input type="password" value={configPat} onChange={e => setConfigPat(e.target.value)} className="w-full px-3 py-2 bg-stone-100 border border-stone-300 rounded-lg text-gray-900" placeholder="ghp_xxxxxxxxxxxxxxxxxxxx" />
              <p className="text-xs text-stone-500 mt-1">Needs: repo, workflow, read:org scopes</p>
            </div>
            <button onClick={handleSaveConfig} disabled={!configPat || configLoading} className="w-full py-2 bg-admin-primary text-white rounded-lg hover:bg-admin-primary/90 disabled:opacity-50">
              {configLoading ? <span className="inline-flex items-center gap-2"><RubyGemMark size={16} drawing color="currentColor" />Saving...</span> : 'Save & Connect'}
            </button>
          </div>
        </div>
      </div>
      </StaffPage>
    );
  }

  return (
    <StaffPage
      section="System"
      icon={GitBranch}
      title="Deployments"
      subtitle="CI/CD pipelines across all repos"
      actions={
        <>
          <button
            onClick={() => handleDeployAll('dev')}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3 py-2 rounded-lg"
            title="Deploy all repos to dev in phased order"
          >
            <Rocket className="w-4 h-4" /> Deploy All to Dev
          </button>
          <button
            onClick={() => handleDeployAll('prod')}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-3 py-2 rounded-lg"
            title="Deploy all repos to production in phased order"
          >
            <Rocket className="w-4 h-4" /> Deploy All to Prod
          </button>
          <button
            onClick={handleRefreshFromGitHub}
            disabled={refreshing || !org}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-3 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Pull latest status from GitHub for all repos"
          >
            {refreshing ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="w-4 h-4" />}
            {refreshing ? `Refreshing ${refreshProgress.done}/${refreshProgress.total}…` : 'Refresh from GitHub'}
          </button>
          <button onClick={() => setShowConfigModal(true)} className="p-2 text-gray-400 hover:text-white transition-colors" title="Settings">
            <Settings className="w-5 h-5" />
          </button>
          <button onClick={() => fetchData(false)} disabled={loading} className="p-2 text-gray-400 hover:text-white transition-colors" title="Refresh">
            {loading ? <RubyGemMark size={20} drawing color="currentColor" /> : <RefreshCw className="w-5 h-5" />}
          </button>
        </>
      }
    >
    <div className="space-y-6">
      {/* Legend */}
      <PipelineLegend />

      {/* Deploy All modal + minimized widget are rendered globally in App.tsx
          (via DeployAllProvider) so they survive navigation and page refreshes. */}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Stats Cards */}
      {initialLoad && !stats ? (
        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonStatsCard key={i} />)}
          <RubyLoader variant="overlay" size={32} />
        </div>
      ) : stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <p className="text-stone-400 text-xs uppercase tracking-wider">Total Repos</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalRepos}</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <p className="text-stone-400 text-xs uppercase tracking-wider">Runs 24h</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.deploys24h}</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <p className="text-stone-400 text-xs uppercase tracking-wider">Success Rate</p>
            <p className="text-2xl font-bold text-green-400 mt-1">{stats.successRate}%</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <p className="text-stone-400 text-xs uppercase tracking-wider">Failed 24h</p>
            <p className={`text-2xl font-bold mt-1 ${stats.failed24h > 0 ? 'text-red-400' : 'text-gray-900'}`}>{stats.failed24h}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm text-gray-900">
          <option value="all">All Categories</option>
          <option value="database">Database</option>
          <option value="infrastructure">Infrastructure</option>
          <option value="service">Services</option>
          <option value="frontend">Frontend</option>
          <option value="library">Libraries</option>
          <option value="document">Documents</option>
          <option value="script">Scripts</option>
        </select>
        <select value={productFilter} onChange={e => setProductFilter(e.target.value)} className="bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm text-gray-900">
          <option value={currentProduct}>This Product</option>
          <option value="shared">Shared Only</option>
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search repos..." className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-gray-900 placeholder-stone-500" />
        </div>
      </div>

      {/* Skeleton Loading */}
      {initialLoad && loading && (
        <div className="space-y-4">
          <SkeletonCategory count={1} />
          {/* The gem sits over the first full category, where the eye lands --
              centred on the whole stack it would fall below the fold. */}
          <div className="relative">
            <SkeletonCategory count={4} />
            <RubyLoader variant="overlay" />
          </div>
          <SkeletonCategory count={8} />
          <SkeletonCategory count={2} />
          <SkeletonCategory count={1} />
          <SkeletonCategory count={1} />
          <SkeletonCategory count={2} />
        </div>
      )}

      {/* Repo List by Category */}
      {!initialLoad && grouped && (
        <div className="space-y-4">
          {CATEGORY_ORDER.map(cat => {
            const repos = filterRepos(grouped[cat] || []);
            if (categoryFilter !== 'all' && categoryFilter !== cat) return null;
            if (repos.length === 0 && !search) return null;

            return (
              <div key={cat} className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleCategory(cat)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-stone-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {expandedCategories.includes(cat) ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />}
                    <span className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                      {CATEGORY_LABELS[cat]} ({repos.length})
                    </span>
                  </div>
                </button>

                {expandedCategories.includes(cat) && repos.length > 0 && (
                  <div className="border-t border-stone-200">
                    {/* Table Header */}
                    <div className="grid grid-cols-[1fr_100px_290px_290px_80px] gap-2 px-4 py-2 text-[10px] text-stone-500 uppercase tracking-wider border-b border-stone-200/50">
                      <span>Stack</span>
                      <span className="text-center">Tags</span>
                      <span className="text-center text-emerald-400">Dev Pipeline</span>
                      <span className="text-center text-rose-400">Prod Pipeline</span>
                      <span className="text-center">Actions</span>
                    </div>

                    {repos.map(repo => {
                      const devBuild = resolveBuildStatus(repo.devBuildStatus, repo.devStatus);
                      const prodBuild = resolveBuildStatus(repo.prodBuildStatus, repo.prodStatus);

                      return (
                      <div
                        key={repo.repoName}
                        className="grid grid-cols-[1fr_100px_290px_290px_80px] gap-2 px-4 py-2 hover:bg-stone-100 transition-colors items-center border-b border-stone-200/30 last:border-b-0"
                      >
                        {/* Stack name + repo name */}
                        <div className="min-w-0">
                          <button
                            onClick={() => navigate(`/deployments/${org}/${repo.repoName}`)}
                            className="text-left text-sm text-gray-900 hover:text-admin-primary transition-colors font-medium block truncate"
                          >
                            {repo.displayName || repo.repoName}
                          </button>
                          <button
                            onClick={() => handleCopyRepoName(repo.repoName)}
                            className="flex items-center gap-1 text-[10px] text-stone-500 hover:text-stone-600 transition-colors font-mono mt-0.5"
                            title="Copy repo name"
                          >
                            {repo.repoName}
                            {copiedRepo === repo.repoName ? <Check className="w-2.5 h-2.5 text-green-400" /> : <Copy className="w-2.5 h-2.5" />}
                          </button>
                        </div>

                        {/* Tags Column */}
                        <div className="relative flex flex-col items-center gap-0.5">
                          {(repo.tags && repo.tags.length > 0) ? (
                            <div className="flex flex-wrap gap-0.5 justify-center">
                              {repo.tags.slice(0, 3).map((tag, i) => (
                                <TagPill key={i} tag={tag} />
                              ))}
                              {repo.tags.length > 3 && (
                                <span className="text-[9px] text-stone-500">+{repo.tags.length - 3}</span>
                              )}
                            </div>
                          ) : null}
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingTagsFor(editingTagsFor === repo.repoName ? null : repo.repoName); }}
                            className="p-0.5 text-stone-500 hover:text-stone-600 transition-colors"
                            title="Edit tags"
                          >
                            <Tag className="w-3 h-3" />
                          </button>
                          {editingTagsFor === repo.repoName && (
                            <TagEditor
                              tags={repo.tags || []}
                              onSave={(tags) => handleSaveTags(repo.repoName, tags)}
                              onCancel={() => setEditingTagsFor(null)}
                            />
                          )}
                        </div>

                        {/* Dev Pipeline — current product only */}
                        <div className="flex flex-col gap-0.5">
                          {PRODUCT_REGIONS.filter(pr => pr.id === currentProduct).map(pr => (
                            <PipelineTooltip
                              key={pr.id}
                              tooltipKey={`${repo.repoName}:dev:${pr.id}`}
                              repoLabel={repo.displayName || repo.repoName}
                              env="dev"
                              regionLabel={pr.region}
                              run={repo.lastDevRun}
                              jobs={repo.devBuildJobs}
                              isRunning={devBuild === 'running' || repo.devDeployStatus === 'running'}
                              deployLocked={repo.deployLocked}
                              actions={{
                                onStart: () => openDeployModalFor(repo, 'dev'),
                                onStop: () => handleStopRun(repo, 'dev'),
                                onRerun: () => openDeployModalFor(repo, 'dev'),
                                loadBranchGraph: () => loadBranchGraph(repo.repoName),
                              }}
                            >
                              <PipelineStatus
                                buildStatus={devBuild as any}
                                deployStatus={(repo.devDeployByProduct?.[pr.id] || 'none') as any}
                                env="dev"
                                regionLabel={pr.region}
                                deployedAt={repo.devDeployTimestamps?.[pr.id]}
                              />
                            </PipelineTooltip>
                          ))}
                        </div>

                        {/* Prod Pipeline — current product only */}
                        <div className="flex flex-col gap-0.5">
                          {PRODUCT_REGIONS.filter(pr => pr.id === currentProduct).map(pr => (
                            <PipelineTooltip
                              key={pr.id}
                              tooltipKey={`${repo.repoName}:prod:${pr.id}`}
                              repoLabel={repo.displayName || repo.repoName}
                              env="prod"
                              regionLabel={pr.region}
                              run={repo.lastProdRun}
                              jobs={repo.prodBuildJobs}
                              isRunning={prodBuild === 'running' || repo.prodDeployStatus === 'running'}
                              deployLocked={repo.deployLocked}
                              actions={{
                                onStart: () => openDeployModalFor(repo, 'prod'),
                                onStop: () => handleStopRun(repo, 'prod'),
                                onRerun: () => openDeployModalFor(repo, 'prod'),
                                loadBranchGraph: () => loadBranchGraph(repo.repoName),
                              }}
                            >
                              <PipelineStatus
                                buildStatus={prodBuild as any}
                                deployStatus={(repo.prodDeployByProduct?.[pr.id] || 'none') as any}
                                env="prod"
                                regionLabel={pr.region}
                                deployedAt={repo.prodDeployTimestamps?.[pr.id]}
                              />
                            </PipelineTooltip>
                          ))}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-center gap-1">
                          <button
                            onClick={() => handleLockToggle(repo)}
                            className={`p-1.5 transition-colors ${
                              repo.deployLocked
                                ? 'text-red-400 hover:text-red-300'
                                : 'text-stone-500 hover:text-stone-600'
                            }`}
                            title={repo.deployLocked ? 'Unlock deploys' : 'Lock deploys'}
                          >
                            {repo.deployLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => { setSelectedRepo(repo); setDeployEnv('dev'); setShowDeployModal(true); }}
                            disabled={deployingRepo === repo.repoName || repo.deployLocked}
                            className={`p-1.5 transition-colors ${
                              repo.deployLocked
                                ? 'text-stone-700 cursor-not-allowed'
                                : 'text-stone-400 hover:text-green-400'
                            }`}
                            title={repo.deployLocked ? 'Deploy locked' : 'Deploy'}
                          >
                            {deployingRepo === repo.repoName ? <RubyGemMark size={16} drawing color="currentColor" /> : <Rocket className="w-4 h-4" />}
                          </button>
                          <a
                            href={`https://github.com/${org}/${repo.repoName}/actions`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-stone-400 hover:text-blue-400 transition-colors"
                            title="View on GitHub"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}

                {expandedCategories.includes(cat) && repos.length === 0 && (
                  <div className="px-4 py-6 text-center text-stone-500 text-sm border-t border-stone-200">No repos found</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Deploy Confirmation Modal */}
      {showDeployModal && selectedRepo && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Deploy {selectedRepo.displayName || selectedRepo.repoName}</h3>
            <p className="text-xs text-stone-500 font-mono mb-4">{selectedRepo.repoName}</p>

            {/* Product (locked to current instance) */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">Product</label>
              {(() => {
                const pr = PRODUCT_REGIONS.find(p => p.id === currentProduct)!;
                return (
                  <div className="flex items-center gap-3 p-3 bg-stone-100 rounded-lg border border-stone-200">
                    <span className={`w-2 h-2 rounded-full ${pr.dotColor}`} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{pr.label}</p>
                      <p className="text-[10px] text-stone-500">{pr.region}</p>
                    </div>
                    <span className="ml-auto text-[10px] text-stone-600 uppercase tracking-wider">locked</span>
                  </div>
                );
              })()}
            </div>

            {/* Environment Selection */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">Environment</label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 bg-stone-100 rounded-lg cursor-pointer border border-stone-200 hover:border-green-600 transition-colors">
                  <input type="radio" name="env" value="dev" checked={deployEnv === 'dev'} onChange={() => setDeployEnv('dev')} className="accent-green-500" />
                  <div>
                    <p className="text-gray-900 text-sm font-medium">Deploy to Dev</p>
                    <p className="text-stone-500 text-xs">Deploys from development branch</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 bg-stone-100 rounded-lg cursor-pointer border border-stone-200 hover:border-red-600 transition-colors">
                  <input type="radio" name="env" value="prod" checked={deployEnv === 'prod'} onChange={() => setDeployEnv('prod')} className="accent-red-500" />
                  <div>
                    <p className="text-gray-900 text-sm font-medium">Deploy to Production</p>
                    <p className="text-stone-500 text-xs">Deploys from main branch (requires main)</p>
                  </div>
                </label>
              </div>
            </div>

            {deployEnv === 'prod' && (
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-3 mb-4 text-red-300 text-xs">
                This will trigger a production deployment. Make sure the main branch is up to date.
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setShowDeployModal(false)} className="flex-1 py-2 bg-stone-100 text-stone-600 rounded-lg hover:bg-stone-200">Cancel</button>
              <button
                onClick={handleDeploy}
                className={`flex-1 py-2 text-white rounded-lg ${deployEnv === 'prod' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
              >
                Deploy to {deployEnv === 'prod' ? 'Prod' : 'Dev'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Config Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">GitHub Configuration</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Organization</label>
                <input type="text" value={configOrg} onChange={e => setConfigOrg(e.target.value)} className="w-full px-3 py-2 bg-stone-100 border border-stone-300 rounded-lg text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Personal Access Token</label>
                <input type="password" value={configPat} onChange={e => setConfigPat(e.target.value)} className="w-full px-3 py-2 bg-stone-100 border border-stone-300 rounded-lg text-gray-900" placeholder="Enter new PAT to update" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowConfigModal(false)} className="flex-1 py-2 bg-stone-100 text-stone-600 rounded-lg hover:bg-stone-200">Cancel</button>
              <button onClick={handleSaveConfig} disabled={!configPat || configLoading} className="flex-1 py-2 bg-admin-primary text-white rounded-lg hover:bg-admin-primary/90 disabled:opacity-50">
                {configLoading ? <span className="inline-flex items-center justify-center gap-2"><RubyGemMark size={16} drawing color="currentColor" />Saving...</span> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Lock/Unlock Modal */}
      {lockModal && (
        <ConfirmLockModal
          repoName={lockModal.repoName}
          locking={lockModal.locking}
          onSuccess={handleLockSuccess}
          onClose={() => setLockModal(null)}
        />
      )}
    </div>
    </StaffPage>
  );
}

export default DeploymentDashboard;
